import dynamic from 'next/dynamic'
import { FC, useCallback, useEffect, useState } from 'react'

import {
  CollectionBoxFragment,
  CreateTradeRequestInput,
  ItemDetailSelectQuery,
  Maybe,
  UserItemBoxFragment,
  UserItemSerial,
  UserTradableUserItemsConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { Steps } from '@/components/organisms/functionals'
import { useForm, UseFormProps, useRouter } from '@/core'
import { handleChunkLoadError } from '@/lib/errorHandler'

type RequestTradeProps = {
  serialNumberSelect: {
    portfolio: Maybe<UserItemBoxFragment>
    loading: boolean
  }
  userTradableUserItems: {
    loading: boolean
    fetching: boolean
    hasNextPage: boolean
    collections: Maybe<{ edges: { node: CollectionBoxFragment }[] }>
    items: Maybe<UserTradableUserItemsConnectionFragment>
  }
  tradeItemDetailSelect: {
    loading: boolean
    portfolio: Maybe<ItemDetailSelectQuery['portfolio']>
  }
  onGetUserTradableUserItems: () => Promise<void>
  onFetchMoreUserTradableUserItems: () => Promise<void>
  onGetTradeItemDetailSelect: (itemId: string) => Promise<void>
  onCreateRequestTrade: (values: CreateTradeRequestInput) => Promise<string>
}

type RequestTradeValues = CreateTradeRequestInput

const schema: UseFormProps['schema'] = o => ({
  requestItemId: o.string(),
  providableItems: o.array().items(
    o.object({
      itemId: o.string(),
      serialId: o.string(),
    })
  ),
  candidateUserId: o.string(),
  candidateItems: o.array().items(
    o.object({
      itemId: o.string(),
      serialId: o.string(),
    })
  ),
  candidateSerialNumber: o.string().optional(),
  requestedByUserId: o.string(),
  requestComment: {
    artistId: o.string().optional().allow('', null),
    content: o.string().min(10).max(200),
  },
  requestSerialNumber: o.string().optional(),
})

export const RequestTrade: FC<RequestTradeProps> = ({
  serialNumberSelect,
  userTradableUserItems,
  tradeItemDetailSelect,
  onGetUserTradableUserItems,
  onFetchMoreUserTradableUserItems,
  onGetTradeItemDetailSelect,
  onCreateRequestTrade,
}) => {
  const { router } = useRouter()
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [candidateSerialNumber, setCandidateSerialNumber] = useState<string>('')
  const [requestSerialNumber, setRequestSerialNumber] = useState<string>('')
  const [tradeNegotiationId, setTradeNegotiationId] = useState<string>('')
  const {
    handleSubmit,
    setValue,
    getValues,
    register,
    formState: { isSubmitting, isValid, errors },
  } = useForm<RequestTradeValues>({
    schema,
    defaultValues,
  })

  useEffect(() => {
    if (!serialNumberSelect.portfolio) {
      return
    }
    setValue('candidateItems.0.itemId', serialNumberSelect.portfolio.item.id)
    setValue('requestItemId', serialNumberSelect.portfolio.item.id)
    setValue('candidateUserId', serialNumberSelect.portfolio.user.id)
    // check length of serials = 1 then hidden step 0
    if (serialNumberSelect.portfolio.serials.length === 1) {
      setValue(
        'candidateItems.0.serialId',
        serialNumberSelect.portfolio.serials[0].serialId
      )
      setCandidateSerialNumber(
        serialNumberSelect.portfolio.serials[0].formattedSerialNumber ?? ''
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNumberSelect.portfolio?.serials])

  const handleGoBack = useCallback(() => {
    if (
      serialNumberSelect.portfolio?.serials?.length === 1 &&
      currentStep === 1
    ) {
      router.goBack()
      return
    }
    if (
      tradeItemDetailSelect.portfolio?.serials?.length === 1 &&
      currentStep === 4
    ) {
      setCurrentStep(2)
      return
    }
    setCurrentStep(prevState => (prevState > 0 ? --prevState : 0))
  }, [
    serialNumberSelect.portfolio?.serials?.length,
    currentStep,
    tradeItemDetailSelect.portfolio?.serials?.length,
    router,
  ])

  const handleSelectCandidateSerial = useCallback(
    async (
      serial: Pick<UserItemSerial, 'formattedSerialNumber' | 'serialId'>
    ) => {
      setValue('candidateItems.0.serialId', serial.serialId)
      setCandidateSerialNumber(serial.formattedSerialNumber ?? '')
      setCurrentStep(1)
    },
    [setValue]
  )

  const handleProvidableItemSelect = useCallback(
    (itemId: string) => {
      setValue('providableItems.0.itemId', itemId)
      setCurrentStep(2)
    },
    [setValue]
  )

  const handleItemDetailSelect = useCallback(async () => {
    if (tradeItemDetailSelect.portfolio?.serials?.length === 1) {
      setCurrentStep(4)
      setValue(
        'providableItems.0.serialId',
        tradeItemDetailSelect.portfolio?.serials[0].serialId
      )
      setRequestSerialNumber(
        tradeItemDetailSelect.portfolio?.serials[0].formattedSerialNumber ?? ''
      )
    } else {
      setCurrentStep(3)
    }
    setValue(
      'requestedByUserId',
      tradeItemDetailSelect.portfolio?.user?.id ?? ''
    )
  }, [
    setValue,
    tradeItemDetailSelect.portfolio?.serials,
    tradeItemDetailSelect.portfolio?.user?.id,
  ])

  const handleSelectRequestSerial = useCallback(
    async (
      serial: Pick<UserItemSerial, 'formattedSerialNumber' | 'serialId'>
    ) => {
      setValue('providableItems.0.serialId', serial.serialId)
      setRequestSerialNumber(serial.formattedSerialNumber ?? '')
      setCurrentStep(4)
    },
    [setValue]
  )

  const handleCreateRequestTrade = useCallback(
    async ({ requestComment, ...values }: CreateTradeRequestInput) => {
      const dataValues = requestComment?.content
        ? {
            ...values,
            requestComment: {
              content: requestComment.content,
              artistId: values.requestedByUserId,
            },
          }
        : values

      return await onCreateRequestTrade(dataValues).then(res => {
        setTradeNegotiationId(res)
      })
    },
    [onCreateRequestTrade]
  )
  return (
    <Steps value={currentStep} sx={{ height: '100%' }}>
      <SerialNumberSelect
        {...serialNumberSelect}
        onTradeConfirm={handleSelectCandidateSerial}
        onClose={router.goBack}
      />
      <ProvidableItemSelect
        {...userTradableUserItems}
        onClose={handleGoBack}
        onGetUserTradableUserItems={onGetUserTradableUserItems}
        onFetchMore={onFetchMoreUserTradableUserItems}
        onProvidableItemSelect={handleProvidableItemSelect}
      />
      <TradeItemDetailSelect
        {...tradeItemDetailSelect}
        onItemDetailSelect={handleItemDetailSelect}
        itemId={getValues('providableItems.0.itemId')}
        onGetTradeItemDetailSelect={onGetTradeItemDetailSelect}
        onClose={handleGoBack}
      />
      <SerialNumberSelect
        {...tradeItemDetailSelect}
        onTradeConfirm={handleSelectRequestSerial}
        onClose={handleGoBack}
      />
      <TradeRequestConfirm
        loading={isSubmitting}
        register={register}
        isValid={isValid}
        formErrors={errors}
        campaign={null}
        tradeNegotiationId={tradeNegotiationId}
        serialNumberSelect={serialNumberSelect}
        tradeItemDetailSelect={tradeItemDetailSelect}
        onClose={handleGoBack}
        candidateSerialNumber={candidateSerialNumber}
        requestSerialNumber={requestSerialNumber}
        onSubmit={handleSubmit(handleCreateRequestTrade)}
      />
    </Steps>
  )
}

const defaultValues: RequestTradeValues = {
  requestedByUserId: '',
  providableItems: [{ itemId: '', serialId: '' }],
  candidateUserId: '',
  candidateItems: [{ itemId: '', serialId: '' }],
  requestItemId: '',
  requestComment: {
    artistId: '',
    content: '',
  },
}

const SerialNumberSelect = dynamic(() => {
  const promise = import('./SerialNumberSelect')
    .then(mod => mod.SerialNumberSelect)
    .catch(async error => {
      await handleChunkLoadError(error)
      return () => null
    })
  return promise
})

const ProvidableItemSelect = dynamic(() => {
  const promise = import('./TradeRequestProvidableItemSelect')
    .then(mod => mod.TradeRequestProvidableItemSelect)
    .catch(async error => {
      await handleChunkLoadError(error)
      return () => null
    })
  return promise
})

const TradeItemDetailSelect = dynamic(() => {
  const promise = import('./TradeItemDetailSelect')
    .then(mod => mod.TradeItemDetailSelect)
    .catch(async error => {
      await handleChunkLoadError(error)
      return () => null
    })
  return promise
})

const TradeRequestConfirm = dynamic(() => {
  const promise = import('./TradeRequestConfirm')
    .then(mod => mod.TradeRequestConfirm)
    .catch(async error => {
      await handleChunkLoadError(error)
      return () => null
    })
  return promise
})
